import React from 'react';
import { ArrowUpRight, Check, Sparkles } from 'lucide-react';

/**
 * Privè Membership — VISUAL PREVIEW (Round 2)
 * Route: /membership-preview
 * Status: Concept review only. No backend / Stripe / nav links.
 * Round 2 additions: Founder's Note · Privè Card visuals · B&W editorial silhouettes flanking hero.
 */

const tiers = [
  {
    id: 'essential',
    name: 'Privè Essential',
    tagline: 'An entry into the ritual.',
    price: 249,
    walkInValue: 270,
    minTerm: '3-month minimum',
    cta: 'Request an Invitation',
    accent: 'ivory',
    cardCode: 'ESS',
    included: [
      '1 Signature Sculpt or RF Ritual',
      '1 Lymphatic Renewal Therapy',
      'Priority booking — 7 days early',
      '15% off all à la carte treatments',
      'Complimentary post-treatment infusion',
      'Birthday-month lymphatic ritual',
    ],
  },
  {
    id: 'signature',
    name: 'Privè Signature',
    tagline: 'The flagship transformation cadence.',
    price: 449,
    walkInValue: 560,
    minTerm: '6-month minimum',
    cta: 'Request an Invitation',
    accent: 'blush',
    cardCode: 'SIG',
    flagship: true,
    included: [
      '2 Signature contouring treatments',
      '1 Lymphatic Renewal',
      '1 RF or Electroporation ritual',
      'Quarterly Privè body assessment',
      '1 complimentary guest pass / quarter',
      'Member-only atelier evenings',
      'All Essential benefits included',
    ],
  },
  {
    id: 'atelier',
    name: 'Privè Atelier',
    tagline: 'By invitation. By application.',
    price: 799,
    walkInValue: 890,
    minTerm: '12-month commitment',
    cta: 'Apply for Privè Atelier',
    accent: 'platinum',
    cardCode: 'ATL',
    invitationOnly: true,
    included: [
      '4 treatments / month — your selection',
      'Annual Meal Prep by Our Chef credit',
      'Dedicated concierge text line',
      'First access to new modalities',
      'Annual head-to-toe protocol review',
      'Invitation to the annual Privè dinner',
      'All Signature benefits included',
    ],
  },
];

/* ---------------------------------------------------------------------------
   Privè Card — luxury membership card mock (Amex Centurion-inspired)
--------------------------------------------------------------------------- */
const PriveCard = ({ tier }) => {
  const isEssential = tier.accent === 'ivory';
  const isSignature = tier.accent === 'blush';
  const isAtelier = tier.accent === 'platinum';

  const surface = isAtelier
    ? 'bg-[#070708]'
    : isSignature
      ? 'bg-[#15131a]'
      : 'bg-[#0f0f12]';

  const foilEdge = isAtelier
    ? 'border-platinum/50'
    : isSignature
      ? 'border-blush/50'
      : 'border-ivory/30';

  const foilText = isAtelier
    ? 'text-platinum'
    : isSignature
      ? 'text-blush'
      : 'text-ivory';

  return (
    <div
      data-testid={`prive-card-${tier.id}`}
      className={`relative ${surface} border ${foilEdge} aspect-[1.586/1] w-full max-w-[360px] mx-auto rounded-[6px] overflow-hidden transition-all duration-500 hover:translate-y-[-2px]`}
      style={{
        boxShadow: isAtelier
          ? '0 20px 60px -20px rgba(0,0,0,0.8), inset 0 1px 0 rgba(201,201,204,0.08)'
          : isSignature
            ? '0 20px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(236,138,174,0.1)'
            : '0 20px 60px -20px rgba(0,0,0,0.6), inset 0 1px 0 rgba(245,241,235,0.06)',
      }}
    >
      {/* Subtle foil texture overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.04]"
        style={{
          backgroundImage:
            'repeating-linear-gradient(115deg, transparent 0px, transparent 2px, rgba(255,255,255,0.4) 2px, rgba(255,255,255,0.4) 3px)',
        }}
      />

      {/* Top hairline */}
      <div className={`absolute top-0 left-6 right-6 h-px ${isAtelier ? 'bg-platinum/30' : isSignature ? 'bg-blush/30' : 'bg-ivory/20'}`} />

      {/* Card content */}
      <div className="relative h-full flex flex-col justify-between p-6">
        {/* Top row: engraved Snatched Beauties monogram + tier code */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 object-contain"
              style={{
                filter: isAtelier
                  ? 'grayscale(100%) brightness(2) opacity(0.85)'
                  : isSignature
                    ? 'sepia(0.3) saturate(1.4) hue-rotate(310deg) brightness(1.1)'
                    : 'grayscale(100%) brightness(1.6) opacity(0.9)',
                mixBlendMode: 'screen',
              }}
            />
            <div className="leading-none">
              <p className={`font-serif text-[11px] tracking-[0.32em] uppercase ${foilText}`}>
                Snatched
              </p>
              <p className={`font-serif italic text-[13px] mt-1 ${isSignature ? 'chrome-rose' : foilText}`}>
                Beauties Privé
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-[9px] tracking-[0.32em] uppercase ${foilText} font-medium`}>
              {tier.name.split(' ')[1]}
            </p>
            <div className={`mt-1 h-px w-12 ml-auto ${isAtelier ? 'bg-platinum/40' : isSignature ? 'bg-blush/40' : 'bg-ivory/30'}`} />
          </div>
        </div>

        {/* Middle: member name + ID */}
        <div className="space-y-3">
          <p className={`font-serif text-lg tracking-wide ${foilText}`}>
            Jane Smith
          </p>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[8px] tracking-[0.3em] uppercase text-platinum/50 mb-1">
                Member No.
              </p>
              <p className={`font-mono text-xs tracking-[0.2em] ${foilText}`}>
                PRV • 0001 • {tier.cardCode}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[8px] tracking-[0.3em] uppercase text-platinum/50 mb-1">
                Member Since
              </p>
              <p className={`font-mono text-xs tracking-[0.2em] ${foilText}`}>02 • 2026</p>
            </div>
          </div>
        </div>
      </div>

      {/* Atelier-only: corner sparkle mark */}
      {isAtelier && (
        <div className="absolute top-3 right-3 opacity-50">
          <Sparkles className="w-3 h-3 text-platinum" strokeWidth={1} />
        </div>
      )}

      {/* Bottom hairline */}
      <div className={`absolute bottom-0 left-6 right-6 h-px ${isAtelier ? 'bg-platinum/30' : isSignature ? 'bg-blush/30' : 'bg-ivory/20'}`} />
    </div>
  );
};

/* ---------------------------------------------------------------------------
   Tier Pricing Card
--------------------------------------------------------------------------- */
const TierCard = ({ tier }) => {
  const savings = Math.round(((tier.walkInValue - tier.price) / tier.walkInValue) * 100);

  const accentText =
    tier.accent === 'blush'
      ? 'text-blush'
      : tier.accent === 'platinum'
        ? 'text-platinum'
        : 'text-ivory';

  const accentBorder =
    tier.accent === 'blush'
      ? 'border-blush/40'
      : tier.accent === 'platinum'
        ? 'border-platinum/30'
        : 'border-ivory/20';

  const bgTone =
    tier.accent === 'platinum'
      ? 'bg-[#0d0d0f]'
      : tier.accent === 'blush'
        ? 'bg-carbon'
        : 'bg-[#101013]';

  return (
    <div
      data-testid={`membership-tier-${tier.id}`}
      className={`relative ${bgTone} border ${accentBorder} rounded-[2px] p-9 lg:p-11 flex flex-col h-full transition-colors duration-500 hover:border-blush/60`}
    >
      {tier.flagship && (
        <div className="absolute -top-3 left-9">
          <span className="bg-blush text-onyx text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 font-medium">
            Most Selected
          </span>
        </div>
      )}

      {tier.invitationOnly && (
        <div className="absolute -top-3 left-9">
          <span className="bg-onyx text-platinum text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 border border-platinum/40 font-medium inline-flex items-center gap-2">
            <Sparkles className="w-3 h-3" />
            By Invitation
          </span>
        </div>
      )}

      {/* Embossed corner mark */}
      <div className="absolute top-6 right-6 opacity-30">
        <div className="text-[9px] tracking-[0.32em] uppercase text-platinum font-serif italic">
          Privè
        </div>
        <div className="h-px w-8 bg-platinum/40 mt-1 ml-auto" />
      </div>

      <div className="mb-2">
        <p className="eyebrow text-silverx mb-4">
          <span className="inline-block w-6 h-px bg-silverx align-middle mr-2" />
          Membership
        </p>
        <h3 className={`font-serif text-3xl lg:text-[34px] leading-tight ${accentText}`}>
          {tier.name.split(' ')[0]}{' '}
          <span className="brand-italic">{tier.name.split(' ').slice(1).join(' ')}</span>
        </h3>
        <p className="text-platinum/80 text-sm mt-3 italic font-serif">{tier.tagline}</p>
      </div>

      <div className="hairline my-7" />

      <div className="mb-7">
        <div className="flex items-baseline gap-2">
          <span className="font-serif text-5xl lg:text-6xl text-ivory tracking-tight">
            ${tier.price}
          </span>
          <span className="text-platinum/70 text-sm tracking-[0.2em] uppercase">/ month</span>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs text-platinum/60">
          <span className="line-through">${tier.walkInValue} walk-in</span>
          <span className="w-1 h-1 rounded-full bg-platinum/40" />
          <span className="chrome-rose">~{savings}% member rate</span>
        </div>
        <p className="text-[11px] tracking-[0.2em] uppercase text-silverx mt-4">{tier.minTerm}</p>
      </div>

      <div className="hairline mb-7" />
      <ul className="space-y-3.5 mb-9 flex-1">
        {tier.included.map((line, i) => (
          <li key={i} className="flex items-start gap-3 text-platinum text-sm leading-relaxed">
            <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${accentText}`} strokeWidth={1.5} />
            <span>{line}</span>
          </li>
        ))}
      </ul>

      <button
        data-testid={`membership-cta-${tier.id}`}
        className={`group/btn w-full inline-flex items-center justify-between gap-3 px-6 py-4 rounded-full border transition-all duration-300 ${
          tier.flagship
            ? 'bg-ivory text-onyx border-ivory hover:bg-blush hover:border-blush'
            : tier.invitationOnly
              ? 'bg-transparent text-ivory border-platinum/60 hover:border-blush hover:text-blush'
              : 'bg-transparent text-ivory border-ivory/30 hover:border-blush hover:text-blush'
        }`}
      >
        <span className="text-[11px] tracking-[0.28em] uppercase">{tier.cta}</span>
        <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:rotate-45" />
      </button>
    </div>
  );
};

/* ---------------------------------------------------------------------------
   Founder's Note — A Letter from Leenda
--------------------------------------------------------------------------- */
const FoundersNote = () => (
  <section
    data-testid="founders-note"
    className="relative py-20 lg:py-28 bg-onyx"
  >
    <div className="max-w-3xl mx-auto px-6 sm:px-10 text-center">
      <p className="eyebrow chrome-rose mb-8">
        <span className="inline-block w-6 h-px bg-blush align-middle mr-2" />
        A Letter from the Founder
        <span className="inline-block w-6 h-px bg-blush align-middle ml-2" />
      </p>

      <h2 className="font-serif text-ivory text-3xl sm:text-4xl lg:text-5xl leading-[1.15] mb-10">
        A note from{' '}
        <span className="brand-italic chrome-rose">Leenda.</span>
      </h2>

      <div className="space-y-6 text-platinum text-base lg:text-lg leading-loose font-serif italic">
        <p>
          At Snatched Beauties Privé, luxury is not just about aesthetics — it's
          about intention, consistency, and curated care.
        </p>
        <p>
          I created Privè Membership for clients who are serious about their
          transformation and want more than occasional appointments. This is for
          the woman committed to investing in herself, maintaining her results,
          and experiencing body contouring as part of a lifestyle — not a
          one-time service.
        </p>
        <p>
          Every membership tier was designed to provide structure,
          accountability, priority access, and elevated support so your wellness
          and sculpting journey feels seamless, exclusive, and results-driven.
        </p>
        <p>
          When you join Privè, you're not simply booking treatments — you're
          entering a more intentional standard of care.
        </p>
        <p className="not-italic font-serif text-ivory">
          Welcome to elevated self-investment.{' '}
          <span className="brand-italic chrome-rose">Welcome to Privè.</span>
        </p>
      </div>

      {/* Signature */}
      <div className="mt-14 flex flex-col items-center gap-3">
        <p
          className="text-blush leading-none"
          style={{
            fontFamily: '"Pinyon Script", "Allura", "Brush Script MT", cursive',
            fontSize: 'clamp(56px, 7vw, 88px)',
            lineHeight: 1,
          }}
        >
          Leenda
        </p>
        <div className="hairline w-24 mt-2" />
        <p className="text-[10px] tracking-[0.32em] uppercase text-silverx">
          Founder · Snatched Beauties Privé
        </p>
      </div>
    </div>
  </section>
);

/* ---------------------------------------------------------------------------
   Privè Card Showcase
--------------------------------------------------------------------------- */
const CardShowcase = () => (
  <section
    data-testid="prive-card-showcase"
    className="relative py-24 lg:py-32 bg-[#08080a] border-y border-pewter"
  >
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
      <div className="text-center max-w-2xl mx-auto mb-20">
        <p className="eyebrow text-silverx mb-6">
          <span className="inline-block w-6 h-px bg-silverx align-middle mr-2" />
          The Privè Card
        </p>
        <h2 className="font-serif text-ivory text-4xl lg:text-5xl leading-tight">
          A signature, <span className="brand-italic chrome-rose">in your hand.</span>
        </h2>
        <p className="text-platinum text-base lg:text-lg leading-relaxed mt-6">
          Every member receives a Privè card — issued by tier, marked with your
          member number, and recognized at every booking, ritual, and atelier
          evening.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-10 lg:gap-12 items-center">
        {tiers.map((tier) => (
          <div key={tier.id} className="flex flex-col items-center gap-5">
            <PriveCard tier={tier} />
            <p className={`font-serif text-lg ${tier.accent === 'blush' ? 'text-blush' : tier.accent === 'platinum' ? 'text-platinum' : 'text-ivory'}`}>
              {tier.name.split(' ')[0]}{' '}
              <span className="brand-italic">{tier.name.split(' ').slice(1).join(' ')}</span>
            </p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------------------------------------------------------------------
   Main Preview Page
--------------------------------------------------------------------------- */
const MembershipPreview = () => {
  return (
    <div className="min-h-screen bg-onyx">
      {/* Preview-only banner */}
      <div className="bg-[#1a1a1d] border-b border-pewter">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-3 flex items-center justify-between gap-4">
          <p className="text-[10px] tracking-[0.3em] uppercase text-blush">
            <span className="inline-block w-6 h-px bg-blush align-middle mr-2" />
            Preview · Concept Review · Round 2
          </p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-platinum/60 hidden sm:block">
            No backend · No payments · For visual approval only
          </p>
        </div>
      </div>

      {/* HERO with B&W silhouettes flanking */}
      <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        {/* Left silhouette — torso/curve */}
        <div
          className="hidden md:block absolute left-0 top-0 bottom-0 w-[28%] lg:w-[24%] pointer-events-none"
          aria-hidden="true"
        >
          <img
            src="https://images.unsplash.com/photo-1767972463246-aaf13f2ad8a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600&sat=-100"
            alt=""
            className="w-full h-full object-cover opacity-25"
            style={{
              filter: 'grayscale(100%) contrast(1.1) brightness(0.6)',
              maskImage: 'linear-gradient(to right, black 30%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to right, black 30%, transparent 100%)',
            }}
          />
        </div>

        {/* Right silhouette — bodyform */}
        <div
          className="hidden md:block absolute right-0 top-0 bottom-0 w-[28%] lg:w-[24%] pointer-events-none"
          aria-hidden="true"
        >
          <img
            src="https://images.unsplash.com/photo-1624923157576-e5e20dfe9a59?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600&sat=-100"
            alt=""
            className="w-full h-full object-cover opacity-25"
            style={{
              filter: 'grayscale(100%) contrast(1.1) brightness(0.6)',
              maskImage: 'linear-gradient(to left, black 30%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to left, black 30%, transparent 100%)',
            }}
          />
        </div>

        {/* Soft radial glow */}
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(circle at 50% 0%, rgba(236,138,174,0.10) 0%, transparent 55%)',
          }}
        />

        <div className="relative max-w-4xl mx-auto px-6 sm:px-8 text-center">
          <p className="eyebrow chrome-rose mb-8">
            <span className="inline-block w-8 h-px bg-blush align-middle mr-3" />
            The Atelier Collection
            <span className="inline-block w-8 h-px bg-blush align-middle ml-3" />
          </p>
          <h1 className="font-serif text-ivory text-5xl sm:text-6xl lg:text-[84px] leading-[1.02] tracking-tight">
            Privè{' '}
            <span className="brand-italic chrome-rose">Membership</span>
          </h1>
          <p className="mt-8 text-platinum text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
            A private invitation to monthly transformation. Three tiers — each
            curated, each intentional. Priority access, exclusive rituals, and
            concierge-level care reserved for the few.
          </p>

          <div className="flex items-center justify-center gap-4 mt-12">
            <div className="h-px w-16 bg-platinum/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-blush/60" />
            <div className="h-px w-32 bg-platinum/30" />
            <div className="w-1.5 h-1.5 rounded-full bg-blush/60" />
            <div className="h-px w-16 bg-platinum/30" />
          </div>
        </div>
      </section>

      {/* Founder's Note */}
      <FoundersNote />

      {/* Privè Card Showcase */}
      <CardShowcase />

      {/* Tiers (Pricing) */}
      <section className="relative py-24 lg:py-32 bg-onyx">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="eyebrow text-silverx mb-6">
              <span className="inline-block w-6 h-px bg-silverx align-middle mr-2" />
              Choose Your Cadence
            </p>
            <h2 className="font-serif text-ivory text-4xl lg:text-5xl leading-tight">
              Three tiers. <span className="brand-italic chrome-rose">One standard.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-7 lg:gap-9 items-stretch">
            {tiers.map((tier) => (
              <TierCard key={tier.id} tier={tier} />
            ))}
          </div>

          {/* Footnote / governance */}
          <div className="mt-20 max-w-3xl mx-auto text-center">
            <div className="hairline mb-10" />
            <p className="text-platinum/70 text-sm leading-loose">
              Members may substitute treatments for any of equal or lesser value
              within their tier. Cross-tier upgrades available with the
              difference applied. One pause per calendar year (up to 30 days)
              honored for vacation, medical, or personal reasons. Privè Atelier
              admission by application — final approval reserved by the Founder.
            </p>
            <p className="mt-8 text-[10px] tracking-[0.32em] uppercase text-silverx">
              Snatched Beauties Privé · Atelier Membership ·{' '}
              <span className="brand-italic font-serif normal-case tracking-normal text-blush">
                est. Los Angeles
              </span>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MembershipPreview;
